/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';

import { LIBELLE_TYPE_ADRESSE } from '@/constants';
import { Sexe } from '@/types';

/**
 * Miroir de `formatters.ts` : là où les `format*` traduisent le formulaire vers
 * l'API, les `parse*` traduisent la **lecture** de la fiche (à plat :
 * `naissanceDate`, `sexeCode`, `naissance*`) vers la shape attendue par les
 * composants de `@mairistem/administres-utils` (`jour/mois/anneeNaissance`,
 * `sexe`, `adresseNaissance` imbriquée).
 */

/** Inverse de `formatDateNaissance` : `'YYYY-MM-DD'` → les 3 champs de la lib. */
export const parseDateNaissance = (date: string | undefined | null) => {
  const parsed = _.isEmpty(date) ? null : dayjs(date);
  if (_.isNil(parsed) || !parsed.isValid()) {
    return { jourNaissance: null, moisNaissance: null, anneeNaissance: null };
  }
  return {
    jourNaissance: parsed.date(),
    moisNaissance: parsed.month() + 1,
    anneeNaissance: parsed.year(),
  };
};

/** Motifs de sexe, féminins d'abord (cf. `parseSexe`). */
const MOTIFS_FEMININS = ['MME', 'MADAME', 'FEMME', 'F'];
const MOTIFS_MASCULINS = ['M.', 'MONSIEUR', 'HOMME', 'M', 'H'];

const correspond = (valeur: string, motifs: string[]) =>
  motifs.some((motif) => _.startsWith(valeur, motif));

/**
 * Inverse de `formatSexe`, tolérant aux trois codifications que l'API peut
 * renvoyer pour un même individu :
 * - `1` / `2` (contrat de la création) ;
 * - `sexe` en libellé court, via l'accesseur du modèle : `'M.'` / `'Mme'` ;
 * - `sexeCode` en libellé long : `'Monsieur'` / `'Madame'` / `'Indéterminé'`.
 *
 * ⚠️ Les motifs **féminins sont testés avant les masculins**, et sur les DEUX
 * champs. C'est indispensable : `'MADAME'` et `'MME'` commencent tous deux par
 * `'M'`. L'implémentation précédente ne gardait `'MME'` que sur `sexeCode` —
 * qui vaut `'Madame'`, pas `'Mme'` — donc `'MADAME'` matchait `'M'` et toute
 * mère était lue comme un homme. Conséquences observées : civilité « Monsieur »
 * sur la fiche, et sexe erroné pré-rempli dans `ModalParent`, réécrit en base à
 * l'enregistrement.
 */
export const parseSexe = (individu: Record<string, any> | undefined) => {
  const sexe = individu?.sexe;

  if (sexe === 1) {
    return Sexe.Homme;
  }
  if (sexe === 2) {
    return Sexe.Femme;
  }

  const valeurs = [sexe, individu?.sexeCode]
    .map((valeur) => _.toUpper(_.toString(valeur)))
    .filter((valeur) => !_.isEmpty(valeur));

  if (valeurs.some((valeur) => correspond(valeur, MOTIFS_FEMININS))) {
    return Sexe.Femme;
  }
  if (valeurs.some((valeur) => correspond(valeur, MOTIFS_MASCULINS))) {
    return Sexe.Homme;
  }

  return undefined;
};

/**
 * Quintuplet `naissance*` de la lecture → adresse imbriquée attendue par
 * `LieuNaissance` / `NaissanceForm`.
 */
export const parseAdresseNaissance = (
  individu: Record<string, any> | undefined,
) => ({
  codeDepartement: individu?.naissanceDepartementCode,
  libelleDepartement: individu?.naissanceDepartementLibelle,
  libelleCodePostal: individu?.naissanceCodePostal,
  libelleCommune: individu?.naissanceLocalite,
  libellePays: individu?.naissancePays,
});

/**
 * Lieu de reconnaissance lu sur l'acte (objet imbriqué `lieuReconnaissance`) →
 * shape attendue par `LieuNaissance` / `NaissanceForm`.
 *
 * ⚠️ Noms de champs différents de `parseAdresseNaissance` : ce n'est pas un
 * individu mais un lieu autonome, dont les clés ne sont pas préfixées
 * `naissance*` (cf. `ActeAdresse::$serializable`).
 */
export const parseAdresseReconnaissance = (
  acte: Record<string, any> | undefined,
) => ({
  codeDepartement: acte?.lieuReconnaissance?.codeDepartement,
  libelleDepartement: acte?.lieuReconnaissance?.libelleDepartement,
  libelleCodePostal: acte?.lieuReconnaissance?.codePostal,
  libelleCommune: acte?.lieuReconnaissance?.localite,
  libellePays: acte?.lieuReconnaissance?.libellePays,
  identifiantCommune: acte?.lieuReconnaissance?.identifiantCommune,
  identifiantDepartement: acte?.lieuReconnaissance?.identifiantDepartement,
  identifiantPays: acte?.lieuReconnaissance?.identifiantPays,
});

/**
 * Lieu d'un acte (`ec_acte_adresse`) → l'objet qu'attend `ChampLieu`.
 *
 * Généralise `parseAdresseReconnaissance` : un mariage porte DEUX lieux
 * (`lieuActe`, `lieuMariage`), la clé est donc un paramètre.
 */
export const parseLieuActe = (
  acte: Record<string, any> | undefined,
  cle: string,
) => ({
  codeDepartement: acte?.[cle]?.codeDepartement,
  libelleDepartement: acte?.[cle]?.libelleDepartement,
  libelleCodePostal: acte?.[cle]?.codePostal,
  libelleCommune: acte?.[cle]?.localite,
  libellePays: acte?.[cle]?.libellePays,
  identifiantCommune: acte?.[cle]?.identifiantCommune,
  identifiantDepartement: acte?.[cle]?.identifiantDepartement,
  identifiantPays: acte?.[cle]?.identifiantPays,
});

/**
 * Inverse de `formatAdressePrefixee` : les champs à plat `{prefixe}*` d'un
 * individu → l'objet qu'attend `AdresseForm` de `@mairistem/adresses`.
 *
 * ⚠️ `type` est reconverti du code entier vers son libellé
 * (`ai_dom_type = 1` → `'Commune'`), sinon les boutons radio du composant
 * n'affichent aucune sélection. Le repli est `'Commune'`, valeur par défaut du
 * composant et de loin la plus fréquente en base.
 */
export const parseAdresseCompletePrefixee = (
  individu: Record<string, any> | undefined,
  prefixe: string,
) => ({
  identifiant: 1,
  type: LIBELLE_TYPE_ADRESSE[Number(individu?.[`${prefixe}Type`])] ?? 'Commune',
  numeroVoirie: individu?.[`${prefixe}NumeroVoirie`] ?? null,
  // Jamais relu de la base : `ai_dom_lettre` n'est pas écrit, son codage
  // n'étant pas établi (cf. `formatAdressePrefixee`). Le champ reste vide.
  indiceRepetition: '',
  complementNumeroVoirie: individu?.[`${prefixe}ComplementNumeroVoirie`] ?? '',
  voie: individu?.[`${prefixe}LibelleVoie`] ?? '',
  identifiantVoie: individu?.[`${prefixe}IdVoie`] ?? null,
  complementDistribution: individu?.[`${prefixe}ComplementDistribution`] ?? '',
  complementIdentification:
    individu?.[`${prefixe}ComplementIdentification`] ?? '',
  complementGeographique: individu?.[`${prefixe}ComplementGeographique`] ?? '',
  codePostal: individu?.[`${prefixe}CodePostal`] ?? '',
  identifiantCodePostal: individu?.[`${prefixe}IdCodePostal`] ?? null,
  article: individu?.[`${prefixe}Article`] ?? null,
  commune: individu?.[`${prefixe}Localite`] ?? '',
  identifiantCommune: individu?.[`${prefixe}IdCommune`] ?? null,
  codeDepartement: individu?.[`${prefixe}DepartementCode`] ?? '',
  departement: individu?.[`${prefixe}DepartementLibelle`] ?? '',
  identifiantDepartement: individu?.[`${prefixe}IdDepartement`] ?? null,
  pays: individu?.[`${prefixe}Pays`] ?? '',
  identifiantPays: individu?.[`${prefixe}IdPays`] ?? null,
});

/** L'individu lu porte-t-il au moins un champ de ce bloc de lieu ? */
const aUnLieu = (individu: Record<string, any> | undefined, prefixe: string) =>
  [
    'DepartementCode',
    'DepartementLibelle',
    'CodePostal',
    'Localite',
    'Pays',
  ].some((suffixe) => !_.isNil(individu?.[`${prefixe}${suffixe}`]));

/**
 * L'individu porte-t-il un domicile ? Le jeu de champs est plus large que celui
 * d'un lieu : un domicile peut n'avoir qu'une voie, sans commune renseignée.
 */
const aUnDomicile = (individu: Record<string, any> | undefined) =>
  [
    'domicileType',
    'domicileNumeroVoirie',
    'domicileLibelleVoie',
    'domicileCodePostal',
    'domicileLocalite',
    'domicilePays',
    'domicileDepartementCode',
  ].some((cle) => !_.isNil(individu?.[cle]));

/**
 * Lieu d'un individu sous un préfixe quelconque (`deces`, `domicile`) →
 * shape attendue par `NaissanceForm`, comme `parseAdresseNaissance`.
 *
 * Symétrique de `formatLieuIndividuPrefixe` : ce sont les noms sérialisés
 * d'`ec_acte_individu` (`${prefixe}Localite`…), déjà au format final — pas de
 * `naissancePaysLibelle` à retraduire ici.
 */
export const parseAdressePrefixee = (
  individu: Record<string, any> | undefined,
  prefixe: string,
) => ({
  codeDepartement: individu?.[`${prefixe}DepartementCode`],
  libelleDepartement: individu?.[`${prefixe}DepartementLibelle`],
  libelleCodePostal: individu?.[`${prefixe}CodePostal`],
  libelleCommune: individu?.[`${prefixe}Localite`],
  libellePays: individu?.[`${prefixe}Pays`],
});

/**
 * Individu de la fiche (à plat) → shape attendue par `EtatCivil`,
 * `PrenomsInput` et `LieuNaissance`. Chaque modal ne garde ensuite que les
 * champs de son périmètre via `_.pick(..., CHAMPS_EDITABLES)`.
 */
export const parseIndividuFiche = (
  individu: Record<string, any> | undefined,
) => ({
  nomFamille: individu?.nomFamille,
  nomUsage: individu?.nomUsage,
  prenom: individu?.prenom,
  prenomsComplementaires: individu?.prenomsComplementaires,
  sexe: parseSexe(individu),
  ...parseDateNaissance(individu?.naissanceDate),
  adresseNaissance: parseAdresseNaissance(individu),
  profession: individu?.profession,
  /*
   * Propres à l'acte de DÉCÈS, et émis SEULEMENT si l'individu les porte : la
   * shape rendue pour une naissance ou une reconnaissance reste donc identique
   * au caractère près. Ce n'est pas de la cosmétique — `buildIndividuUpdate\
   * Payload` teste `data?.adresseDomicile &&` pour décider s'il réécrit ce
   * lieu ; un objet toujours présent, même vide, ferait passer ce test partout.
   */
  ...(_.has(individu, 'age') && { age: individu?.age }),
  ...(aUnLieu(individu, 'deces') && {
    adresseDeces: parseAdressePrefixee(individu, 'deces'),
  }),
  // Le domicile est une adresse COMPLÈTE (voie incluse), pas un simple lieu.
  ...(aUnDomicile(individu) && {
    adresseDomicile: parseAdresseCompletePrefixee(individu, 'domicile'),
  }),
});
